<template>
  <div class="add-fields flex flex-col h-full">
    <div class="clip-radius min-h-0 flex flex-col">
      <div class="border-b border-b-white field-wrapper">
        <Input
          v-model="search"
          class="search-field"
          type="text"
          variant="narrow"
          :label="$t('toolbar.addFields.searchPlaceholder')"
          :aria-label="$t('toolbar.addFields.searchPlaceholder')"
        >
          <template #icon>
            <SearchIcon />
          </template>
        </Input>
      </div>

      <div class="flex justify-end p-3">
        <p class="hover:text-red cursor-pointer text-sm" @click="toggleAll">
          {{ allSelected ? t('toolbar.addFields.deselectAll') : t('toolbar.addFields.selectAll') }}
        </p>
      </div>

      <article class="form-box">
        <!-- Groups-->
        <section v-for="[name, group] in groups" :key="name">
          <span class="text-pre text-light-gray">{{ name }}</span>
          <menu class="flex flex-col gap-y-2 mt-3">
            <li v-for="field in group" :key="field.key" class="text-neutral">
              <checkbox
                :model-value="visible[field.key]"
                :label="field.title"
                name="column"
                :value="field.key"
                @update:model-value="apply(field.key, $event)"
              />
            </li>
          </menu>
        </section>
      </article>
    </div>

    <div class="pt-10">
      <CustomButton
          variant="secondary"
          :busy="savingAsDefault"
          @click="saveAsDefault"
      >
        {{ $t("toolbar.addFields.saveAsDefault") }}
      </CustomButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, ref, unref, watch,} from 'vue';
import SearchIcon from '@images/search.svg?component';
import {useLocalStorage, useVModel} from '@vueuse/core';
import Input from '@components/utils/inputs/Input.vue';
import Checkbox from '@components/utils/inputs/Checkbox.vue';
import {useI18n} from 'vue-i18n';
import CustomButton from '@utils/CustomButton.vue';
import {useFetchAction} from '@hooks/fetch';
import {tableFieldEntriesKey} from '@hooks/table-configuration';
import {useRouter} from 'vue-router';
import {groupBy} from '@/utils/ds';
import {useToolbar} from '../toolbar/injects';
import {useNotify} from '../notifications';
import type {TableFieldsEntry, WorkingTableFieldsEntry} from '@/entities/Entities';

export interface Field {
  key: string;
  title?: string;
  visible?: boolean;
  group: string;
}

export interface Props {
  modelValue: Record<string, boolean>;
  fields: Field[];
}

const search = ref('');

const emit = defineEmits(['update:modelValue']);
const props = defineProps<Props>();
const visible = useVModel(props, 'modelValue', emit, { passive: true });

const toolbar = useToolbar();

const notify = useNotify();
const { t } = useI18n();

const filteredFields = computed(() => props.fields.filter(
  (field) => !search.value || field.title?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase()),
));

const groups = computed(() => {
  const map = groupBy(filteredFields.value || [], (v) => v.group);
  return Array.from(map.entries());
});

const apply = (key: string, newVal: boolean) => {
  visible.value = {
    ...visible.value,
    [key]: newVal,
  };
};

const [exec, { loading: savingAsDefault }] = useFetchAction();

const router = useRouter();

const saveAsDefault = async () => {
  try {
    const tableFieldsEntries = useLocalStorage<Record<string, WorkingTableFieldsEntry>>(tableFieldEntriesKey, {});

    const tableFieldsForPage = typeof router.currentRoute.value.name === 'string'
      ? unref(tableFieldsEntries)?.[router.currentRoute.value.name]
      : undefined;

    let response;

    if (tableFieldsForPage?.id) {
      response = await exec<TableFieldsEntry>(tableFieldsForPage.id, {
        method: 'PUT',
        body: {
          activeFields: Object.keys(visible.value).filter((key) => visible.value[key]),
        },
      });
    } else {
      response = await exec<TableFieldsEntry>('/table_fields_entries', {
        method: 'POST',
        body: {
          activeFields: Object.keys(visible.value).filter((key) => visible.value[key]),
          pathKey: router.currentRoute.value.name,
        },
      });
    }

    if (response && typeof router.currentRoute.value.name === 'string') {
      tableFieldsEntries.value = {
        ...tableFieldsEntries.value,
        [router.currentRoute.value.name]: {
          id: response['@id'],
          activeFields: response.activeFields,
        },
      };
    }

    notify?.({
      title: t('toolbar.addFields.saveDefaultSuccess'),
      variant: 'success',
    });
  } catch (e) {
    notify?.({
      title: t('toolbar.addFields.saveDefaultFailed'),
      variant: 'error',
    });
  }
};

const allSelected = computed(() => props.fields.every((field) => visible.value[field.key]));

const toggleAll = () => {
  const newObject: Record<string, boolean> = {};
  if  (allSelected.value) {
    props.fields.forEach((field) => {
      newObject[field.key] = false;
    });
  } else {
    props.fields.forEach((field) => {
      newObject[field.key] = true;
    });
  }
  visible.value = newObject;
}
</script>

<style lang="scss" scoped>
.column-switches {
  @apply w-full bg-table-head-gray rounded-8;
  @apply flex flex-col;

  svg {
    @apply absolute right-0 top-1/2 -translate-y-1/2;
    @apply fill-current stroke-current;

    width: px(18);
    height: px(18);
  }
}

.search-field {
  @apply w-full text-body-regular;

  --input-bg: transparent;
  --input-border: 0;
  --input-border-top-left-radius: #{theme("borderRadius.8")};
  --input-border-top-right-radius: #{theme("borderRadius.8")};
  --input-border-bottom-left-radius: 0;
  --input-border-bottom-right-radius: 0;
  --input-padding: #{px(14)} #{px(20)};
  --input-label-bg: transparent;
  --input-outline: none;
  --input-active-color: none;
  --input-active-label-offset: 0;

  &::placeholder {
    @apply text-neutral;
  }
}

.form-box {
  @apply h-full px-5 pb-6;
  @apply overflow-y-auto overscroll-y-contain;
  @apply flex flex-col gap-y-5;
}

.clip-radius {
  @include absolutePseudo("before") {
    @apply bg-table-head-gray;
    @apply w-full h-full;
    @apply pointer-events-none;
    @apply rounded-8 overflow-hidden;
    z-index: -1;
  }
}
</style>
